import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, RefreshCw } from "lucide-react";

import { Button } from "components/Graphs/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import CopyField from "components/UI-lib/CopyField";
import { AddLinuxUserDialog } from "./AddLinuxUserDialog";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import { useOrganizationMembersQuery } from "server/devplane/organizations/queries";
import { isOrgAdminOrMaintainerRole } from "utils/roleUtils";
import { toast } from "utils/toast";
import { LinuxUserSetupInstructions } from "../LinuxUserSetupInstructions";
import { ManagedSetupSection } from "./ComputeNodeSetupControls";
import { ENABLE_SSH_COMMAND, GRANT_SSH_COMMAND } from "./utils";
import {
  getSshAccessUserScope,
  SshAccessScopePill,
  SshAccessScopeNote,
  SshAccessUserGrantSelector,
} from "./SshAccessControls";
import type { SshAccessUserScope } from "./SshAccessControls";

export const SshAccessSetupSection: React.FC<{
  sshAccess: Array<{ userId: string; linuxUser: string }>;
  isSSHEnabled: boolean;
  usesCertAuthSSHProvider: boolean;
  onCheckSSHAccess: () => Promise<void>;
  onGrantUser: (userId: string, linuxUser: string) => Promise<void>;
  onRevokeUser: (userId: string, linuxUser: string) => Promise<void>;
}> = ({
  sshAccess,
  isSSHEnabled,
  usesCertAuthSSHProvider,
  onCheckSSHAccess,
  onGrantUser,
  onRevokeUser,
}) => {
  const { activeOrgId, activeOrgRole } = useContext(OrgContext);
  const { me } = useContext(UserContext);
  const { data: teamMembers = [], isSuccess: hasMemberData } =
    useOrganizationMembersQuery(activeOrgId);
  const [userScope, setUserScope] = useState<SshAccessUserScope>("none");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [pendingUserIds, setPendingUserIds] = useState<string[]>([]);
  const [pendingRevocationCount, setPendingRevocationCount] = useState(0);
  const [selectedLinuxUser, setSelectedLinuxUser] = useState<string | null>(
    null,
  );
  const [sshAction, setSSHAction] = useState<"idle" | "checking" | "updating">(
    "idle",
  );
  const initializedLinuxUserRef = useRef<string | null>(null);
  const hasSSHAccess = isSSHEnabled;
  const hasSSHAccessGrants = sshAccess.length > 0;
  const shouldManageSSHAccess = hasSSHAccess && hasSSHAccessGrants;
  const currentOrgMember = useMemo(() => {
    const normalizedEmail = me?.email.trim().toLowerCase();
    return teamMembers.find(
      (member) =>
        member.id === me?.id ||
        (normalizedEmail !== "" &&
          member.email.trim().toLowerCase() === normalizedEmail),
    );
  }, [me?.email, me?.id, teamMembers]);
  const currentUserId = currentOrgMember?.id ?? me?.id;
  const canManageSSHAccess =
    isOrgAdminOrMaintainerRole(activeOrgRole) &&
    (usesCertAuthSSHProvider ||
      !hasSSHAccessGrants ||
      sshAccess.some((access) => access.userId === currentUserId));
  const availableLinuxUsers = useMemo(
    () =>
      Array.from(
        new Set(sshAccess.map((access) => access.linuxUser).filter(Boolean)),
      ).sort((first, second) => first.localeCompare(second)),
    [sshAccess],
  );
  const activeLinuxUser = availableLinuxUsers.includes(selectedLinuxUser ?? "")
    ? selectedLinuxUser!
    : availableLinuxUsers[0];
  const activeLinuxUserAccess = useMemo(
    () => sshAccess.filter((access) => access.linuxUser === activeLinuxUser),
    [activeLinuxUser, sshAccess],
  );
  const activeLinuxUserAccessIds = useMemo(
    () =>
      Array.from(new Set(activeLinuxUserAccess.map((access) => access.userId))),
    [activeLinuxUserAccess],
  );
  const initialUserScope = useMemo<SshAccessUserScope>(() => {
    return getSshAccessUserScope(
      activeLinuxUserAccessIds,
      currentUserId,
      teamMembers,
    );
  }, [activeLinuxUserAccessIds, currentUserId, teamMembers]);

  useEffect(() => {
    if (activeLinuxUser && sshAction !== "updating") {
      setUserScope(initialUserScope);
    }
  }, [activeLinuxUser, initialUserScope, sshAction]);

  useEffect(() => {
    if (
      !activeLinuxUser ||
      initializedLinuxUserRef.current === activeLinuxUser
    ) {
      return;
    }

    initializedLinuxUserRef.current = activeLinuxUser;
    setSelectedUserIds(activeLinuxUserAccessIds);
    setPendingUserIds([]);
  }, [activeLinuxUser, activeLinuxUserAccessIds]);
  const handleUserScopeChange = (scope: SshAccessUserScope) => {
    if (scope === "some") {
      setSelectedUserIds(activeLinuxUserAccessIds);
    } else {
      setSelectedUserIds([]);
      setPendingUserIds([]);
    }
    setUserScope(scope);
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserIds((userIds) =>
      userIds.includes(userId) ? userIds : [...userIds, userId],
    );
  };
  const handleRemoveSelectedUser = (userId: string) => {
    setSelectedUserIds((userIds) =>
      userIds.filter((candidate) => candidate !== userId),
    );
  };
  const handleLinuxUserChange = (linuxUser: string) => {
    const accessUserIds = Array.from(
      new Set(
        sshAccess
          .filter((access) => access.linuxUser === linuxUser)
          .map((access) => access.userId),
      ),
    );

    setSelectedLinuxUser(linuxUser);
    initializedLinuxUserRef.current = linuxUser;
    setUserScope(
      getSshAccessUserScope(accessUserIds, currentUserId, teamMembers),
    );
    setSelectedUserIds(accessUserIds);
    setPendingUserIds([]);
  };

  const usersToGrant = useMemo(() => {
    const desiredUserIds =
      userScope === "everyone"
        ? teamMembers.map((member) => member.id)
        : userScope === "some"
          ? selectedUserIds
          : currentUserId
            ? [currentUserId]
            : [];

    return desiredUserIds.filter(
      (userId) => !activeLinuxUserAccessIds.includes(userId),
    );
  }, [
    activeLinuxUserAccessIds,
    currentUserId,
    selectedUserIds,
    teamMembers,
    userScope,
  ]);
  const usersToRevoke = useMemo(() => {
    const desiredUserIds =
      userScope === "everyone"
        ? teamMembers.map((member) => member.id)
        : userScope === "some"
          ? selectedUserIds
          : currentUserId
            ? [currentUserId]
            : [];
    const desiredUserIdSet = new Set(desiredUserIds);

    return activeLinuxUserAccessIds.filter(
      (userId) => !desiredUserIdSet.has(userId),
    );
  }, [
    activeLinuxUserAccessIds,
    currentUserId,
    selectedUserIds,
    teamMembers,
    userScope,
  ]);
  const hasSSHAccessChanges =
    usersToGrant.length > 0 || usersToRevoke.length > 0;

  const handleCheckSSHAccess = async () => {
    if (sshAction !== "idle") return;

    try {
      setSSHAction("checking");
      await onCheckSSHAccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Unable to check SSH access.",
      );
    } finally {
      setSSHAction("idle");
    }
  };

  const handleSSHAccess = async () => {
    if (sshAction !== "idle") return;

    if (!shouldManageSSHAccess) {
      await handleCheckSSHAccess();
      return;
    }

    try {
      const linuxUser =
        activeLinuxUser ??
        activeLinuxUserAccess.find((entry) => entry.userId === currentUserId)
          ?.linuxUser;

      if (hasSSHAccessChanges && !linuxUser) {
        throw new Error(
          "SSH access needs to be enabled for your Linux user before it can be shared.",
        );
      }

      if (hasSSHAccessChanges) {
        setSSHAction("updating");
        setPendingUserIds(usersToGrant);
        setPendingRevocationCount(usersToRevoke.length);

        const accessChanges = [
          ...usersToGrant.map((userId) => ({ type: "grant" as const, userId })),
          ...usersToRevoke.map((userId) => ({
            type: "revoke" as const,
            userId,
          })),
        ];
        const results = await Promise.allSettled(
          accessChanges.map(async ({ type, userId }) => {
            if (type === "grant") {
              await onGrantUser(userId, linuxUser!);
            } else {
              await onRevokeUser(userId, linuxUser!);
            }
            return { type, userId };
          }),
        );
        const failed = results.flatMap((result, index) =>
          result.status === "rejected" ? [accessChanges[index]!] : [],
        );

        if (failed.length > 0) {
          toast.error("Unable to update SSH access for some users.");
          return;
        }
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Unable to update SSH access.",
      );
    } finally {
      setPendingUserIds([]);
      setPendingRevocationCount(0);
      setSSHAction("idle");
    }
  };

  return (
    <ManagedSetupSection title="SSH Access">
      {!canManageSSHAccess ? (
        <p className="text-sm leading-[1.6] text-zinc-400">
          You do not have access to manage SSH access to this device.
        </p>
      ) : (
        <>
          {shouldManageSSHAccess ? (
            <p className="mb-3 text-sm leading-[1.6] text-zinc-400">
              Manage SSH access to this node
            </p>
          ) : null}
          {shouldManageSSHAccess ? (
            <>
              <div className="flex flex-col gap-[18px]">
                <div>
                  <label
                    id="node-added-linux-user-label"
                    className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-500"
                  >
                    Linux user
                  </label>
                  <div className="flex w-full max-w-sm items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-labelledby="node-added-linux-user-label"
                          disabled={!activeLinuxUser || sshAction !== "idle"}
                          className="flex h-9 min-w-0 flex-1 items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-left text-sm text-zinc-200 disabled:cursor-not-allowed disabled:text-zinc-500"
                        >
                          <span>
                            {activeLinuxUser ?? "No Linux users available"}
                          </span>
                          <ChevronDown aria-hidden="true" className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
                      >
                        <DropdownMenuRadioGroup
                          value={activeLinuxUser}
                          onValueChange={handleLinuxUserChange}
                        >
                          {availableLinuxUsers.map((linuxUser) => (
                            <DropdownMenuRadioItem
                              key={linuxUser}
                              value={linuxUser}
                              indicator="check"
                            >
                              {linuxUser}
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      type="button"
                      variant="secondary"
                      aria-label="Refresh SSH access"
                      title="Refresh SSH access"
                      className="h-9 w-9 shrink-0 bg-transparent p-0 hover:bg-zinc-800"
                      disabled={sshAction !== "idle"}
                      isLoading={sshAction === "checking"}
                      onClick={() => void handleCheckSSHAccess()}
                    >
                      {sshAction === "checking" ? null : (
                        <RefreshCw aria-hidden="true" className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <AddLinuxUserDialog idPrefix="node-added-add-linux-user" />
                </div>
                <section>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-500">
                    User access
                  </div>
                  <div
                    role="radiogroup"
                    aria-label="Users granted SSH access"
                    className={`flex flex-wrap gap-2 ${userScope === "some" ? "mb-3" : ""}`}
                  >
                    <SshAccessScopePill
                      selected={userScope === "none"}
                      label="Just me"
                      onClick={() => handleUserScopeChange("none")}
                    />
                    <SshAccessScopePill
                      selected={userScope === "everyone"}
                      label="Team"
                      onClick={() => handleUserScopeChange("everyone")}
                    />
                    <SshAccessScopePill
                      selected={userScope === "some"}
                      label="Some individuals"
                      onClick={() => handleUserScopeChange("some")}
                    />
                  </div>
                  {userScope === "none" && usersToRevoke.length > 0 ? (
                    <SshAccessScopeNote>
                      Only you will have SSH access.{" "}
                      {(sshAction === "updating"
                        ? pendingRevocationCount
                        : usersToRevoke.length
                      ).toLocaleString()}{" "}
                      {(sshAction === "updating"
                        ? pendingRevocationCount
                        : usersToRevoke.length) === 1
                        ? "user"
                        : "users"}{" "}
                      will lose SSH access.
                    </SshAccessScopeNote>
                  ) : userScope === "everyone" ? (
                    <SshAccessScopeNote>
                      {hasSSHAccessChanges
                        ? `All ${teamMembers.length} users currently in this organization will be granted SSH access.`
                        : `All ${teamMembers.length} users in this organization already have SSH access.`}
                      <span className="mt-1 block font-bold">
                        Users added to your organization later will not be
                        granted SSH access automatically.
                      </span>
                    </SshAccessScopeNote>
                  ) : userScope === "some" ? (
                    <SshAccessUserGrantSelector
                      members={teamMembers}
                      selectedUserIds={selectedUserIds}
                      pendingUserIds={pendingUserIds}
                      hasMemberData={hasMemberData}
                      onSelect={handleSelectUser}
                      onRemoveSelected={handleRemoveSelectedUser}
                    />
                  ) : null}
                </section>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-sm font-medium text-white">
                  1. Confirm that the active Linux user on your device is the
                  user you want to enable SSH access for.
                </p>
                <LinuxUserSetupInstructions
                  idPrefix="node-added-linux-user"
                  className="mt-3"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-white">2. Enable SSH</p>
                <div className="mt-3 min-w-0 overflow-hidden">
                  <CopyField
                    value={ENABLE_SSH_COMMAND}
                    analytics={{ userid: "", eventLabel: "brev enable-ssh" }}
                    className="w-full"
                  />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-white">
                  3. Grant yourself SSH access
                </p>
                <div className="mt-3 min-w-0 overflow-hidden">
                  <CopyField
                    value={GRANT_SSH_COMMAND}
                    analytics={{ userid: "", eventLabel: "brev grant-ssh" }}
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mt-[22px] flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              className="gap-2"
              disabled={
                sshAction !== "idle" ||
                (shouldManageSSHAccess && !hasSSHAccessChanges)
              }
              isLoading={
                shouldManageSSHAccess
                  ? sshAction === "updating"
                  : sshAction === "checking"
              }
              loadingText={
                sshAction === "checking"
                  ? "Checking SSH access..."
                  : "Updating SSH access..."
              }
              onClick={() => void handleSSHAccess()}
            >
              {shouldManageSSHAccess ? (
                "Update SSH Access"
              ) : (
                <>
                  <RefreshCw aria-hidden="true" className="h-4 w-4" />
                  Check SSH access
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </ManagedSetupSection>
  );
};
